import type { RemoteViewLayout } from 'shared/lib/tv-game/nativeLayout.ts';
import {
  getRemoteCalibStandZoneMetrics,
  type CalibStandZoneKind,
} from 'shared/lib/tv-game/remoteCalibStandZone.ts';

export function drawCalibBackdrop(ctx: CanvasRenderingContext2D, layout: RemoteViewLayout): void {
  const {
    offsetX, offsetY, gameWidth, gameHeight,
  } = layout;
  const overlay = ctx.createLinearGradient(offsetX, offsetY, offsetX, offsetY + gameHeight);
  overlay.addColorStop(0, 'rgba(4, 12, 38, 0.90)');
  overlay.addColorStop(0.6, 'rgba(7, 20, 58, 0.86)');
  overlay.addColorStop(1, 'rgba(4, 8, 28, 0.92)');
  ctx.fillStyle = overlay;
  ctx.fillRect(offsetX, offsetY, gameWidth, gameHeight);
}

/** Body silhouettes shown inside the stand zone (served from `public/media`). */
const SILHOUETTE_SRC: Record<CalibStandZoneKind, string> = {
  // `upper` (fruit-slice) uses the torso→head cut; everything else uses the full body.
  upper: '/media/calibSilhouetteUpper.svg',
  full: '/media/calibSilhouetteFull.svg',
  square: '/media/calibSilhouetteFull.svg',
};
const silhouetteCache = new Map<string, HTMLImageElement>();

function getSilhouette(kind: CalibStandZoneKind): HTMLImageElement {
  const src = SILHOUETTE_SRC[kind];
  let img = silhouetteCache.get(src);
  if (!img) {
    img = new Image();
    img.src = src;
    silhouetteCache.set(src, img);
  }
  return img;
}

function roundRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  if (typeof ctx.roundRect === 'function') {
    ctx.roundRect(x, y, w, h, r);
  } else {
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }
}

/**
 * Stand-zone card shown on each game's start / calibration screen.
 * Mirrors the native apps' `Spotlight`: black 0.7 cut-out card, body silhouette aligned
 * to the bottom, solid white border. `kind` selects the silhouette + box geometry.
 */
export function drawRemoteCalibrationStandZone(
  ctx: CanvasRenderingContext2D,
  layout: RemoteViewLayout,
  kind: CalibStandZoneKind = 'full',
): void {
  const {
    boxX, boxY, boxW, boxH,
  } = getRemoteCalibStandZoneMetrics(layout, kind);
  // Native uses a fixed 20dp corner; scale it to the TV box so it reads the same.
  const radius = Math.min(boxW, boxH) * 0.05;
  const borderW = Math.max(3, layout.gameHeight * 0.006);

  ctx.save();

  // Dark rounded card (native: Black @ 0.7).
  roundRectPath(ctx, boxX, boxY, boxW, boxH, radius);
  ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
  ctx.fill();

  // Body silhouette — contained, centred horizontally, aligned to the bottom (as in native).
  const img = getSilhouette(kind);
  if (img.complete && img.naturalWidth > 0) {
    const scale = Math.min(boxW / img.naturalWidth, boxH / img.naturalHeight);
    const drawW = img.naturalWidth * scale;
    const drawH = img.naturalHeight * scale;
    const drawX = boxX + (boxW - drawW) / 2;
    const drawY = boxY + (boxH - drawH);
    ctx.save();
    roundRectPath(ctx, boxX, boxY, boxW, boxH, radius);
    ctx.clip();
    ctx.drawImage(img, drawX, drawY, drawW, drawH);
    ctx.restore();
  }

  // Solid white border (native: white 4dp).
  roundRectPath(ctx, boxX, boxY, boxW, boxH, radius);
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = borderW;
  ctx.stroke();

  ctx.restore();
}
